import { theme } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useAppBarScrollOverEffect, useAppBarStickyHeightValue } from "~/components/app-bar/hooks";
import { useArticleListContext } from "~/components/article/article-list/contexts";
import type { ArticleRenderChildProps } from "~/components/article/article-render";
import { GlobalChecker } from "~/components/article/article-render/global-checker";
import { useMobileInlineAdSlotIsDisabledFlag } from "~/components/article/article-render/hooks";
import { BlockquoteLongRead } from "~/components/article/article-render/long-read-article/blockquote-long-read";
import { ClientSideSuspense } from "~/components/common/client-side-suspense";
import type { longReadArticleArticle$key } from "~/queries/__generated__/longReadArticleArticle.graphql";

import {
  AppBarScrollOverTracker,
  AuthorDetail,
  CommentAndReaction,
  Content,
  ContentFooterAdSlot,
  FooterAdSlot,
  HeaderAdSlot,
  LandingBanner,
  Left,
  LongReadArticleContainer,
  LongReadArticleWrapper,
  MoreOnThis,
  Social,
  SocialDesktop,
  SocialMobile,
  SpeechPlayer,
  SummaryAndAuthor,
  Topic,
} from "./styles";

export type Props = ArticleRenderChildProps & {
  isOlympicArticle: boolean;
  reference: longReadArticleArticle$key;
};

export const LongReadArticle: FunctionComponent<Props> = ({
  isOlympicArticle,
  provider,
  reference: reference_,
}) => {
  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  const { firstArticleEntityId } = useArticleListContext();
  const article = useFragment(
    graphql`
      fragment longReadArticleArticle on Article {
        entityId
        ...articleLandingBannerArticle
        ...globalCheckerContent
        ...blockquoteLongReadContent
      }
    `,
    reference_,
  );

  const { trackerNode } = useAppBarScrollOverEffect({
    swapTiming: "exit",
    swapVariant: "scmp/generic-light",
    tracker: AppBarScrollOverTracker,
  });

  const isInlineAdSlotDisabled = useMobileInlineAdSlotIsDisabledFlag();

  return (
    <LongReadArticleContainer>
      <LongReadArticleWrapper>
        <LandingBanner
          $addAppBarNegativeMargin={article.entityId === firstArticleEntityId}
          reference={article}
          variant="long-read"
        />
        <SummaryAndAuthor>
          {provider.subHeadline()}
          <SpeechPlayer>
            {provider.speechPlayer({})} {provider.speechPlayerPortal({})}
          </SpeechPlayer>
          {provider.authorBlock({ variant: "dark" })}
        </SummaryAndAuthor>

        {trackerNode}

        <Content>
          {provider.body({
            extraRenderFunctionMap: {
              "blockquote-quote": (
                props,
                itemIndex,
                index,
                _children,
                schemaNode,
                parentSchemaNode,
              ) => (
                <BlockquoteLongRead
                  {...props}
                  index={index}
                  key={itemIndex}
                  parentSchemaNode={parentSchemaNode}
                  reference={article}
                  schemaNode={schemaNode}
                />
              ),
            },
            inlineAdSlots: {
              "1": {
                adUnit: "m_lrec1",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "2": {
                adUnit: "d_lrec2",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "3": {
                adUnit: "m_lrec4d",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "4": {
                adUnit: "m_lrec4e",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "5": {
                adUnit: "m_lrec4f",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
            },
          })}
        </Content>
        <CommentAndReaction>{provider.commentTriggerAndReaction()}</CommentAndReaction>
        <Social>
          <SocialMobile>
            {provider.shareWidget({
              variant: "longReadMobile",
            })}
          </SocialMobile>
          <SocialDesktop>
            {provider.shareWidget({
              variant: "longRead",
            })}
          </SocialDesktop>
        </Social>
        <MoreOnThis>
          {isOlympicArticle
            ? provider.olympicWidgets()
            : provider.moreOnThis({ variant: "horizontal" })}
        </MoreOnThis>
        <AuthorDetail>{provider.authorDetails()}</AuthorDetail>
        <Topic>{provider.topics()}</Topic>
        <Left $stickyStackedHeight={appBarStickyHeight}>
          {provider.shareWidget({
            variant: "longReadLeft",
          })}
          {provider.actionWidget()}
        </Left>

        <HeaderAdSlot>
          {provider.adslot({
            adUnit: "d_banner1",
            sizes: [[970, 250], [728, 90], [970, 90], "fluid"],
          })}
        </HeaderAdSlot>

        <ClientSideSuspense>
          <GlobalChecker reference={article}>
            <ContentFooterAdSlot>
              {provider.adslot({
                adUnit: "native4",
                sizes: ["fluid"],
              })}
            </ContentFooterAdSlot>

            <FooterAdSlot>
              {provider.adslot({
                adUnit: "m_lrec3a",
                sizes: [[300, 250], "fluid"],
                withBackground: true,
                withLabel: true,
              })}
            </FooterAdSlot>
          </GlobalChecker>
        </ClientSideSuspense>
      </LongReadArticleWrapper>
    </LongReadArticleContainer>
  );
};

LongReadArticle.displayName = "LongReadArticle";
